import React, { useState, useCallback } from "react";
import { Dialog } from "@headlessui/react";
import { XMarkIcon, VideoCameraIcon } from "@heroicons/react/24/solid";
import { useDropzone, FileRejection } from "react-dropzone";

interface AddDemoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAddDemo: (videos: File[]) => void;
}

export default function AddDemoDialog({
  isOpen,
  onClose,
  onAddDemo,
}: AddDemoDialogProps) {
  const [demoVideos, setDemoVideos] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [fileRejections, setFileRejections] = useState<string | null>(null);

  // Process files whether from input or dropzone
  const processFiles = useCallback((files: File[]) => {
    // Filter to only allow video files and take only the first one
    const validFiles = files.filter((file) => file.type.startsWith("video/"));

    if (validFiles.length === 0) {
      setFileRejections("Please upload a video file (MP4, MOV, etc.).");
      return;
    }

    // Take only the first file
    const fileToUse = [validFiles[0]];
    setDemoVideos(fileToUse);
    setFileRejections(null);

    // Create preview URL for the selected video
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrls([reader.result as string]);
    };
    reader.readAsDataURL(fileToUse[0]);
  }, []);

  // Setup dropzone
  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        setFileRejections("Please upload a valid video file.");
        return;
      }

      processFiles(acceptedFiles);
    },
    [processFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "video/mp4": [".mp4"],
      "video/quicktime": [".mov"],
      "video/x-msvideo": [".avi"],
      "video/webm": [".webm"],
    },
    maxFiles: 1,
  });

  const handleSubmit = () => {
    if (demoVideos.length > 0) {
      onAddDemo(demoVideos);
      resetForm();
      onClose();
    }
  };

  const resetForm = () => {
    setDemoVideos([]);
    setPreviewUrls([]);
    setFileRejections(null);
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop with blur effect */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-all duration-300"
        aria-hidden="true"
      />

      {/* Full-screen container for centering with height constraint and overflow handling */}
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 shadow-xl transition-all duration-300 transform my-8 max-h-[90vh] flex flex-col">
          {/* Close button */}
          <div className="absolute top-4 right-4 z-10">
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200 transition-colors duration-200 focus:outline-none hover:cursor-pointer"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Dialog header - fixed at top */}
          <div className="pt-6 px-6 flex-shrink-0">
            <Dialog.Title className="text-2xl font-bold text-center mb-2 text-gray-900 dark:text-white transition-colors duration-200">
              Upload Demo Video
            </Dialog.Title>
            <Dialog.Description className="text-md text-gray-600 dark:text-gray-300 text-center mb-6 transition-colors duration-200">
              Upload a video to demonstrate your product
            </Dialog.Description>
          </div>

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto px-6 pb-3">
            {/* Video upload area */}
            <div className="mb-6">
              <div
                {...getRootProps()}
                className={`w-full h-48 rounded-xl border-2 border-dashed ${
                  isDragActive
                    ? "border-purple-500 bg-purple-50/70 dark:bg-purple-900/30"
                    : "border-purple-400/50 dark:border-purple-500/50 bg-purple-50/50 dark:bg-purple-900/10"
                } flex flex-col items-center justify-center cursor-pointer hover:border-purple-500/70 dark:hover:border-purple-400/70 hover:bg-purple-50/80 dark:hover:bg-purple-900/20 transition-all duration-300`}
              >
                {previewUrls.length > 0 ? (
                  <div className="w-full h-full p-3 overflow-auto">
                    <video
                      src={previewUrls[0]}
                      controls
                      className="w-full h-full object-contain rounded-lg shadow-sm"
                    />
                  </div>
                ) : (
                  <>
                    <input {...getInputProps()} />
                    <VideoCameraIcon className="h-10 w-10 text-purple-400 dark:text-purple-500 mb-2" />
                    <div>
                      <span className="text-purple-600 dark:text-purple-400 font-medium">
                        Click to upload
                      </span>
                      <span className="text-gray-600 dark:text-gray-300">
                        {" "}
                        or drag and drop
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      MP4, MOV, AVI, WEBM • Max. 20MB
                    </div>
                    {isDragActive && (
                      <div className="mt-2 text-sm text-purple-600 dark:text-purple-400 font-medium">
                        Drop the file here...
                      </div>
                    )}
                  </>
                )}
              </div>
              {fileRejections && (
                <div className="mt-2 text-sm text-red-500 dark:text-red-400">
                  {fileRejections}
                </div>
              )}
            </div>

            {demoVideos.length > 0 && (
              <div className="mb-4 text-sm flex items-center text-gray-600 dark:text-gray-300">
                <div className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full px-2 py-0.5 text-xs font-medium mr-2">
                  {demoVideos.length} video selected
                </div>
                <span className="text-gray-500 dark:text-gray-400 text-xs">
                  Video size: {Math.round(demoVideos[0].size / (1024 * 1024))}{" "}
                  MB
                </span>
              </div>
            )}
          </div>

          {/* Footer - fixed at bottom */}
          <div className="bg-gray-50 dark:bg-gray-800/50 py-4 px-6 flex justify-end items-center transition-colors duration-200 border-t border-gray-100 dark:border-gray-800 flex-shrink-0 rounded-b-2xl ">
            <div className="flex space-x-3">
              <button
                onClick={handleCancel}
                className="px-5 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 text-sm font-medium hover:cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className={`px-5 py-2.5 rounded-lg text-white text-sm font-medium shadow-sm transition-all duration-200 flex items-center ${
                  demoVideos.length === 0
                    ? "bg-purple-400 dark:bg-purple-500/50 cursor-not-allowed opacity-70"
                    : "bg-purple-600 dark:bg-purple-500 hover:bg-purple-700 dark:hover:bg-purple-600 hover:cursor-pointer"
                }`}
                disabled={demoVideos.length === 0}
              >
                Add
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
